import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import './ShippingDocuments.css';

function ShippingDocuments({ salesOrder, scannedItems, boxes, fulfillmentId, onComplete }) {
  const [loading, setLoading] = useState(false);
  const [labelUrl, setLabelUrl] = useState(null);
  const [trackingNumber, setTrackingNumber] = useState(null);
  const [rates, setRates] = useState([]);
  const [selectedService, setSelectedService] = useState('03'); // Default to Ground
  const [loadingRates, setLoadingRates] = useState(false);
  const printRef = useRef();

  // Fetch shipping rates on mount
  React.useEffect(() => {
    fetchShippingRates();
  }, []);

  // Fetch shipping rates
  const fetchShippingRates = async () => {
    setLoadingRates(true);
    try {
      const response = await axios.post('/api/fulfillment/get-rates', {
        shipTo: salesOrder.shipTo,
        packages: boxes.map(box => ({
          weight: box.weight,
          dimensions: box.dimensions,
          type: box.type
        }))
      });

      if (response.data.success && response.data.rates) {
        setRates(response.data.rates);
      }
    } catch (error) {
      console.error('Error fetching rates:', error);
      // Don't show error to user, just use default Ground service
    } finally {
      setLoadingRates(false);
    }
  };

  // Generate shipping label
  const generateShippingLabel = async () => {
    setLoading(true);
    try {
      const response = await axios.post('/api/fulfillment/generate-label', {
        fulfillmentId,
        salesOrderId: salesOrder.id,
        shipTo: salesOrder.shipTo,
        serviceCode: selectedService,
        packages: boxes.map(box => ({
          weight: box.weight,
          dimensions: box.dimensions,
          type: box.type
        }))
      });

      if (response.data.success) {
        // Convert base64 PDF to blob URL
        const labelBase64 = response.data.labelData;
        const byteCharacters = atob(labelBase64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);

        setLabelUrl(url);
        setTrackingNumber(response.data.trackingNumber);
      }
    } catch (error) {
      console.error('Error generating label:', error);
      alert(error.response?.data?.error || 'Failed to generate shipping label');
    } finally {
      setLoading(false);
    }
  };

  // Print shipping label
  const printShippingLabel = () => {
    if (labelUrl) {
      const printWindow = window.open(labelUrl, '_blank');
      printWindow.onload = () => {
        printWindow.print();
      };
    }
  };

  // Print packing list
  const printPackingList = () => {
    window.print();
  };

  // Complete and start new order
  const handleComplete = () => {
    onComplete();
  };

  return (
    <div className="shipping-documents">
      <div className="documents-header">
        <h2>Shipping & Documents</h2>
        <div className="order-info">
          Order #{salesOrder.orderNumber} • {salesOrder.customerName}
        </div>
      </div>

      <div className="documents-content">
        {/* Fulfillment Summary */}
        <div className="fulfillment-summary">
          <h3>Fulfillment Summary</h3>
          <div className="summary-details">
            <div>Fulfillment ID: {fulfillmentId}</div>
            <div>Items Packed: {scannedItems.length}</div>
            <div>Total Boxes: {boxes.length}</div>
            <div>Total Weight: {boxes.reduce((sum, b) => sum + b.weight, 0).toFixed(2)} lbs</div>
          </div>
        </div>

        {/* Shipping Service Selection */}
        {!labelUrl && (
          <div className="shipping-service-section">
            <h3>Select Shipping Service</h3>
            {loadingRates ? (
              <div>Loading rates...</div>
            ) : rates.length > 0 ? (
              <div className="service-options">
                {rates.map((rate) => (
                  <div
                    key={rate.serviceCode}
                    className={`service-option ${selectedService === rate.serviceCode ? 'selected' : ''}`}
                    onClick={() => setSelectedService(rate.serviceCode)}
                  >
                    <div className="service-name">{rate.serviceName}</div>
                    <div className="service-cost">${parseFloat(rate.cost).toFixed(2)}</div>
                    {rate.deliveryDate && (
                      <div className="service-delivery">{rate.deliveryDate} days</div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="default-service">
                <div className="service-option selected">
                  <div className="service-name">UPS Ground</div>
                  <div className="service-cost">Default Service</div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Shipping Label Section */}
        <div className="shipping-label-section">
          <h3>Shipping Label</h3>
          {!labelUrl ? (
            <button
              className="generate-label-button"
              onClick={generateShippingLabel}
              disabled={loading}
            >
              {loading ? 'Generating Label...' : 'Generate Shipping Label'}
            </button>
          ) : (
            <div className="label-ready">
              <div className="tracking-info">
                <strong>Tracking #:</strong> {trackingNumber}
              </div>
              <button
                className="print-label-button"
                onClick={printShippingLabel}
              >
                🖨️ Print Shipping Label
              </button>
            </div>
          )}
        </div>

        {/* Packing List Section */}
        <div className="packing-list-section">
          <h3>Packing List</h3>
          <button
            className="print-packing-button"
            onClick={printPackingList}
          >
            🖨️ Print Packing List
          </button>
        </div>

        {/* Action Buttons */}
        <div className="document-actions">
          <button
            className="complete-order-button"
            onClick={handleComplete}
            disabled={!labelUrl}
          >
            Complete & Start Next Order
          </button>
        </div>
      </div>

      {/* Printable Packing List */}
      <div className="print-only" ref={printRef}>
        <div className="packing-list">
          <div className="packing-header">
            <h1>PACKING LIST</h1>
            <div className="company-logo">Your Company Name</div>
          </div>

          <div className="packing-info">
            <div className="info-section">
              <h4>Ship To:</h4>
              <div>{salesOrder.customerName}</div>
              <div>{salesOrder.shipTo?.address1}</div>
              {salesOrder.shipTo?.address2 && <div>{salesOrder.shipTo.address2}</div>}
              <div>
                {salesOrder.shipTo?.city}, {salesOrder.shipTo?.state} {salesOrder.shipTo?.zip}
              </div>
              <div>{salesOrder.shipTo?.country}</div>
            </div>

            <div className="info-section">
              <h4>Order Information:</h4>
              <div>Order #: {salesOrder.orderNumber}</div>
              <div>Date: {new Date().toLocaleDateString()}</div>
              <div>Fulfillment #: {fulfillmentId}</div>
              {trackingNumber && <div>Tracking #: {trackingNumber}</div>}
            </div>
          </div>

          <table className="packing-items">
            <thead>
              <tr>
                <th>SKU</th>
                <th>Description</th>
                <th>Quantity</th>
              </tr>
            </thead>
            <tbody>
              {scannedItems.reduce((acc, item) => {
                const existing = acc.find(i => i.sku === item.sku);
                if (existing) {
                  existing.quantity += 1;
                } else {
                  acc.push({ ...item, quantity: 1 });
                }
                return acc;
              }, []).map((item, index) => (
                <tr key={index}>
                  <td>{item.sku}</td>
                  <td>{item.description}</td>
                  <td>{item.quantity}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="packing-footer">
            <div>Total Items: {scannedItems.length}</div>
            <div>Boxes: {boxes.length}</div>
            <div>Thank you for your order!</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ShippingDocuments;